import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Landmark, CheckCircle2, Loader2, Clock, XCircle, X } from "lucide-react";
import { fetchOrder } from "../lib/api";
import { fmtARS } from "../lib/utils-app";

async function copy(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  } catch {
    toast.info(`${label}: ${text}`);
  }
}

function useCountdown(iso) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!iso) return;
    const tick = () => setLeft(Math.max(0, new Date(iso).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  const m = Math.floor(left / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function Row({ label, value, copyable, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        <div className={`text-white font-mono text-sm sm:text-base ${testid === "chytapay-cvu" ? "break-all" : "break-words"}`} data-testid={testid}>{value || "—"}</div>
      </div>
      {copyable && value && (
        <button onClick={() => copy(value, label)} data-testid={`${testid}-copy`} className="shrink-0 px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1">
          <Copy className="w-3.5 h-3.5" /> Copiar
        </button>
      )}
    </div>
  );
}

export default function ChytaPayDialog({ order, onClose }) {
  const [status, setStatus] = useState("pending_payment");
  const pollRef = useRef(null);
  const countdown = useCountdown(order?.expires_at);

  useEffect(() => {
    if (!order) return;
    setStatus("pending_payment");
    const tick = async () => {
      try {
        const o = await fetchOrder(order.order_id);
        setStatus(o.status);
        if (o.status !== "pending_payment") clearInterval(pollRef.current);
      } catch {}
    };
    pollRef.current = setInterval(tick, 5000);
    return () => clearInterval(pollRef.current);
  }, [order]);

  useEffect(() => {
    if (!order) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [order, onClose]);

  if (!order) return null;
  const paid = ["paid", "processing", "completed"].includes(status);

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto" style={{ backgroundColor: "rgba(0,0,0,0.85)", WebkitOverflowScrolling: "touch" }} onClick={onClose} data-testid="chytapay-overlay">
      <div className="min-h-full flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" data-testid="chytapay-dialog" className="relative w-full max-w-md rounded-xl border border-[#ff4500]/40 bg-[#0a0a0a] text-white p-5 sm:p-6 shadow-2xl">
        <button onClick={onClose} data-testid="chytapay-close" aria-label="Cerrar" className="absolute right-3 top-3 p-1.5 rounded-md text-neutral-400 hover:text-white hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
        <div className="mb-3 pr-8">
          <h2 className="font-display text-2xl tracking-wider flex items-center gap-2">
            <Landmark className="w-5 h-5 text-[#ff4500]" /> Pagá por transferencia
          </h2>
        </div>
        {paid ? (
          <div className="py-6 text-center" data-testid="chytapay-paid">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
            <div className="font-display text-2xl tracking-wider">{order?.category === "reseller_topup" ? "¡Saldo acreditado!" : "¡Pago recibido!"}</div>
            <p className="text-sm text-neutral-400 mt-1">{order?.category === "reseller_topup" ? "Ya podés usar tu saldo para recargar." : "Tu recarga se está enviando a tu cuenta. Podés seguirla en \"Mis pedidos\"."}</p>
          </div>
        ) : status === "failed" ? (
          <div className="py-6 text-center" data-testid="chytapay-expired">
            <XCircle className="w-14 h-14 text-rose-400 mx-auto mb-3" />
            <div className="font-display text-2xl tracking-wider">Cobro vencido</div>
            <p className="text-sm text-neutral-400 mt-1">No transfieras a este CVU. Generá un pedido nuevo.</p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-neutral-300">
              Transferí el <span className="text-[#ff4500] font-semibold">monto exacto</span> desde tu banco o billetera al CVU. Se acredita solo.
            </p>
            <div className="mt-3 rounded-lg border border-white/10 bg-[#0f0f0f] px-4 py-1">
              <Row label="Monto" value={order ? String(order.amount_ars) : ""} copyable testid="chytapay-amount" />
              <Row label="CVU" value={order?.cvu} copyable testid="chytapay-cvu" />
              <Row label="Titular" value={order?.holder} testid="chytapay-holder" />
            </div>
            <div className="mt-2 text-center font-display text-3xl tracking-wider text-[#ff4500]">{order && fmtARS(order.amount_ars)}</div>
            <div className="mt-3 flex items-center justify-between text-xs text-neutral-400">
              <span className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Esperando tu transferencia...</span>
              <span className="flex items-center gap-1" data-testid="chytapay-countdown"><Clock className="w-3.5 h-3.5" /> Vence en {countdown}</span>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
